import React, { useEffect, useState } from 'react';
import { 
    Form, 
    Input, 
    Select, 
    Switch, 
    InputNumber, 
    Button, 
    Space,
    Row,
    Col,
    Card,
    Divider,
    Tag,
    Tooltip
} from 'antd';
import { 
    SaveOutlined, 
    CloseOutlined,
    PlusCircleOutlined,
    MinusCircleOutlined,
    SwapOutlined,
    BookOutlined,
    LockOutlined,
    RiseOutlined,
    FallOutlined,
    RetweetOutlined,
    ToolOutlined,
    MoreOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { 
    TransactionMenuConfigDTO, 
    TransactionMenuFormValues, 
    TransactionTypeOption 
} from '../types';
import GlAccountPicker, { GlAccountOption } from './GlAccountPicker';

interface TransactionMenuFormProps {
    initialValues?: TransactionMenuConfigDTO | null;
    transactionTypes: TransactionTypeOption[];
    isSubmitting: boolean;
    onSubmit: (values: TransactionMenuFormValues) => void;
    onCancel: () => void;
}

const iconMap: Record<string, React.ReactNode> = {
    PlusCircleOutlined: <PlusCircleOutlined />,
    MinusCircleOutlined: <MinusCircleOutlined />,
    SwapOutlined: <SwapOutlined />,
    BookOutlined: <BookOutlined />,
    LockOutlined: <LockOutlined />,
    RiseOutlined: <RiseOutlined />,
    FallOutlined: <FallOutlined />,
    RetweetOutlined: <RetweetOutlined />,
    ToolOutlined: <ToolOutlined />,
    MoreOutlined: <MoreOutlined />
};

const iconOptions = [
    { value: 'PlusCircleOutlined', label: 'Plus Circle' },
    { value: 'MinusCircleOutlined', label: 'Minus Circle' },
    { value: 'SwapOutlined', label: 'Swap' },
    { value: 'BookOutlined', label: 'Book' },
    { value: 'LockOutlined', label: 'Lock' },
    { value: 'RiseOutlined', label: 'Rise' },
    { value: 'FallOutlined', label: 'Fall' },
    { value: 'RetweetOutlined', label: 'Retweet' },
    { value: 'ToolOutlined', label: 'Tool' },
    { value: 'MoreOutlined', label: 'More' }
];

interface AccountPreview {
    glNo: string;
    acNo?: string;
    name: string;
    nature: string;
}

export const TransactionMenuForm: React.FC<TransactionMenuFormProps> = ({
    initialValues,
    transactionTypes,
    isSubmitting,
    onSubmit,
    onCancel
}) => {
    const [form] = Form.useForm<TransactionMenuFormValues>();
    const isEditing = !!initialValues?.id;
    const selectedTransactionType = Form.useWatch('transactionType', form);
    const isErrorCorrectionType = selectedTransactionType === 'ERROR_CORRECTION';

    // Track account previews for display
    const [drPreview, setDrPreview] = useState<AccountPreview | null>(
        initialValues?.debitGlNo
            ? { glNo: initialValues.debitGlNo, acNo: initialValues.debitAcNo || undefined, name: '', nature: '' }
            : null
    );
    const [crPreview, setCrPreview] = useState<AccountPreview | null>(
        initialValues?.creditGlNo
            ? { glNo: initialValues.creditGlNo, acNo: initialValues.creditAcNo || undefined, name: '', nature: '' }
            : null
    );

    // Track GL-only selections to enable AC search within that GL
    const [drGlOnly, setDrGlOnly] = useState<string>(initialValues?.debitGlNo || '');
    const [crGlOnly, setCrGlOnly] = useState<string>(initialValues?.creditGlNo || '');
    const [drAcNo, setDrAcNo] = useState<string>(initialValues?.debitAcNo || '');
    const [crAcNo, setCrAcNo] = useState<string>(initialValues?.creditAcNo || '');

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                title: initialValues.title,
                transactionType: initialValues.transactionType,
                debitGlNo: initialValues.debitGlNo,
                debitAcNo: initialValues.debitAcNo,
                creditGlNo: initialValues.creditGlNo,
                creditAcNo: initialValues.creditAcNo,
                trxCode: initialValues.trxCode,
                printReceipt: initialValues.printReceipt ?? false,
                defaultAmount: initialValues.defaultAmount,
                workflowLimitAmount: initialValues.workflowLimitAmount,
                forceWorkflowApproval: initialValues.forceWorkflowApproval ?? false,
                icon: initialValues.icon,
                sequence: initialValues.sequence,
                description: initialValues.description,
                branchCode: initialValues.branchCode,
                active: initialValues.active ?? true
            });
            setDrGlOnly(initialValues.debitGlNo || '');
            setCrGlOnly(initialValues.creditGlNo || '');
            setDrAcNo(initialValues.debitAcNo || '');
            setCrAcNo(initialValues.creditAcNo || '');
        } else {
            form.resetFields();
            form.setFieldsValue({ printReceipt: false, active: true, forceWorkflowApproval: false });
            setDrPreview(null);
            setCrPreview(null);
            setDrGlOnly('');
            setCrGlOnly('');
            setDrAcNo('');
            setCrAcNo('');
        }
    }, [initialValues, form]);

    const handleDrGlSelect = (opt: GlAccountOption) => {
        // GL search result: if accountId contains a dot it's a full account (GL.AC), else GL-only
        const isFullAccount = opt.accountId?.includes('.');
        const resolvedAcNo = isFullAccount ? (opt.acNo || opt.accountId.split('.')[1]) : '';
        form.setFieldsValue({ debitGlNo: opt.glNo, debitAcNo: resolvedAcNo || undefined });
        setDrGlOnly(opt.glNo);
        setDrAcNo(resolvedAcNo);
        setDrPreview({ glNo: opt.glNo, acNo: resolvedAcNo || undefined, name: opt.acName || opt.glName, nature: opt.acNature });
    };

    const handleCrGlSelect = (opt: GlAccountOption) => {
        const isFullAccount = opt.accountId?.includes('.');
        const resolvedAcNo = isFullAccount ? (opt.acNo || opt.accountId.split('.')[1]) : '';
        form.setFieldsValue({ creditGlNo: opt.glNo, creditAcNo: resolvedAcNo || undefined });
        setCrGlOnly(opt.glNo);
        setCrAcNo(resolvedAcNo);
        setCrPreview({ glNo: opt.glNo, acNo: resolvedAcNo || undefined, name: opt.acName || opt.glName, nature: opt.acNature });
    };

    const handleDrAcSelect = (opt: GlAccountOption) => {
        const ac = opt.acNo || opt.accountId.split('.')[1];
        form.setFieldsValue({ debitAcNo: ac });
        setDrAcNo(ac);
        setDrPreview({ glNo: opt.glNo, acNo: ac, name: opt.acName || opt.glName, nature: opt.acNature });
    };

    const handleCrAcSelect = (opt: GlAccountOption) => {
        const ac = opt.acNo || opt.accountId.split('.')[1];
        form.setFieldsValue({ creditAcNo: ac });
        setCrAcNo(ac);
        setCrPreview({ glNo: opt.glNo, acNo: ac, name: opt.acName || opt.glName, nature: opt.acNature });
    };

    const clearDrAc = () => {
        form.setFieldsValue({ debitAcNo: undefined });
        setDrAcNo('');
        setDrPreview(prev => prev ? { ...prev, acNo: undefined } : null);
    };

    const clearCrAc = () => {
        form.setFieldsValue({ creditAcNo: undefined });
        setCrAcNo('');
        setCrPreview(prev => prev ? { ...prev, acNo: undefined } : null);
    };

    const handleFinish = (values: TransactionMenuFormValues) => {
        onSubmit(values);
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={{
                printReceipt: false,
                active: true
            }}
        >
            <Card title="Basic Information" size="small" className="mb-4">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="title"
                            label="Menu Title"
                            rules={[
                                { required: true, message: 'Please enter menu title' },
                                { max: 100, message: 'Title must not exceed 100 characters' }
                            ]}
                        >
                            <Input placeholder="Enter menu title" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="transactionType"
                            label="Transaction Type"
                            rules={[{ required: true, message: 'Please select transaction type' }]}
                        >
                            <Select
                                placeholder="Select transaction type"
                                options={transactionTypes.map(t => ({
                                    value: t.value,
                                    label: (
                                        <Space>
                                            {iconMap[t.icon] || <MoreOutlined />}
                                            {t.label}
                                        </Space>
                                    )
                                }))}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="icon"
                            label="Icon"
                        >
                            <Select
                                placeholder="Select icon"
                                allowClear
                                options={iconOptions.map(opt => ({
                                    value: opt.value,
                                    label: (
                                        <Space>
                                            {iconMap[opt.value]}
                                            {opt.label}
                                        </Space>
                                    )
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="sequence"
                            label="Display Order"
                        >
                            <InputNumber 
                                min={0} 
                                placeholder="Auto-assigned if empty"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="description"
                    label="Description"
                >
                    <Input.TextArea 
                        rows={2} 
                        placeholder="Optional description"
                        maxLength={500}
                        showCount
                    />
                </Form.Item>
            </Card>

            <Card
                title={
                    <Space>
                        Account Configuration (Double Entry)
                        <Tooltip title="Configure which GL/accounts are pre-filled on the Dr and Cr sides. If only GL is set, the operator searches for the account at transaction time. If both GL and AC are set, the account is locked.">
                            <InfoCircleOutlined className="text-blue-400" />
                        </Tooltip>
                    </Space>
                }
                size="small"
                className="mb-4"
            >
                {/* DEBIT SIDE */}
                <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded bg-slate-700 flex items-center justify-center">
                            <span className="text-white text-xs font-bold">Dr</span>
                        </div>
                        <span className="font-medium text-slate-700">Debit Account</span>
                        <span className="text-xs text-slate-400">— search by GL name or number</span>
                    </div>
                    <Row gutter={12} align="bottom">
                        <Col span={14}>
                            <Form.Item
                                label="Search GL Account (Debit)"
                                className="mb-0"
                            >
                                <GlAccountPicker
                                    mapType="GL"
                                    value={drPreview ? (drPreview.acNo ? `${drPreview.glNo}.${drPreview.acNo}` : drPreview.glNo) : undefined}
                                    onAccountSelect={handleDrGlSelect}
                                    placeholder="Search debit GL..."
                                />
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item name="debitGlNo" label="GL No" className="mb-0">
                                <Input placeholder="e.g., 100100" className="font-mono" />
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item name="debitAcNo" label="AC No" className="mb-0">
                                <Input placeholder="e.g., 1" className="font-mono" />
                            </Form.Item>
                        </Col>
                    </Row>
                    {/* If GL is set but AC is not — allow searching within that GL */}
                    {drGlOnly && !drAcNo && (
                        <div className="mt-2">
                            <Form.Item
                                label={
                                    <span className="text-slate-500 text-xs">
                                        Pin a specific account in GL {drGlOnly}
                                        <span className="ml-1 text-slate-400">(optional — leave blank so operator picks at transaction time)</span>
                                    </span>
                                }
                                className="mb-0"
                            >
                                <GlAccountPicker
                                    mapType="AC"
                                    glNo={drGlOnly}
                                    onAccountSelect={handleDrAcSelect}
                                    placeholder={`Search member account in GL ${drGlOnly}...`}
                                />
                            </Form.Item>
                        </div>
                    )}
                    {drGlOnly && drAcNo && (
                        <div className="mt-1">
                            <Button size="small" type="link" danger onClick={clearDrAc}>
                                Clear pinned AC — let operator search at transaction time
                            </Button>
                        </div>
                    )}
                    {drPreview?.name && (
                        <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded text-xs flex items-center gap-2">
                            <span className="font-medium text-slate-700">{drPreview.name}</span>
                            <Tag color="blue">{drPreview.nature}</Tag>
                            <span className="text-slate-400">{drPreview.glNo}{drPreview.acNo ? `.${drPreview.acNo}` : ''}</span>
                        </div>
                    )}
                </div>

                <Divider className="my-3" />

                {/* CREDIT SIDE */}
                <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded bg-emerald-700 flex items-center justify-center">
                            <span className="text-white text-xs font-bold">Cr</span>
                        </div>
                        <span className="font-medium text-slate-700">Credit Account</span>
                        <span className="text-xs text-slate-400">— search by GL name or number</span>
                    </div>
                    <Row gutter={12} align="bottom">
                        <Col span={14}>
                            <Form.Item
                                label="Search GL Account (Credit)"
                                className="mb-0"
                            >
                                <GlAccountPicker
                                    mapType="GL"
                                    value={crPreview ? (crPreview.acNo ? `${crPreview.glNo}.${crPreview.acNo}` : crPreview.glNo) : undefined}
                                    onAccountSelect={handleCrGlSelect}
                                    placeholder="Search credit GL..."
                                />
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item name="creditGlNo" label="GL No" className="mb-0">
                                <Input placeholder="e.g., 500100" className="font-mono" />
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item name="creditAcNo" label="AC No" className="mb-0">
                                <Input placeholder="e.g., 1" className="font-mono" />
                            </Form.Item>
                        </Col>
                    </Row>
                    {crGlOnly && !crAcNo && (
                        <div className="mt-2">
                            <Form.Item
                                label={
                                    <span className="text-slate-500 text-xs">
                                        Pin a specific account in GL {crGlOnly}
                                        <span className="ml-1 text-slate-400">(optional — leave blank so operator picks at transaction time)</span>
                                    </span>
                                }
                                className="mb-0"
                            >
                                <GlAccountPicker
                                    mapType="AC"
                                    glNo={crGlOnly}
                                    onAccountSelect={handleCrAcSelect}
                                    placeholder={`Search member account in GL ${crGlOnly}...`}
                                />
                            </Form.Item>
                        </div>
                    )}
                    {crGlOnly && crAcNo && (
                        <div className="mt-1">
                            <Button size="small" type="link" danger onClick={clearCrAc}>
                                Clear pinned AC — let operator search at transaction time
                            </Button>
                        </div>
                    )}
                    {crPreview?.name && (
                        <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded text-xs flex items-center gap-2">
                            <span className="font-medium text-slate-700">{crPreview.name}</span>
                            <Tag color="green">{crPreview.nature}</Tag>
                            <span className="text-slate-400">{crPreview.glNo}{crPreview.acNo ? `.${crPreview.acNo}` : ''}</span>
                        </div>
                    )}
                </div>

                <Divider className="my-3" />

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="trxCode"
                            label="Transaction Code"
                            rules={[{ max: 3, message: 'Transaction code must not exceed 3 characters' }]}
                        >
                            <Input placeholder="e.g., DEP" maxLength={3} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="defaultAmount" label="Default Amount">
                            <InputNumber
                                min={0}
                                precision={2}
                                placeholder="Optional"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="workflowLimitAmount"
                            label={isErrorCorrectionType ? 'Workflow Limit Amount (unused for reversals)' : 'Workflow Limit Amount'}
                        >
                            <InputNumber
                                min={0}
                                precision={2}
                                placeholder={isErrorCorrectionType ? 'Not used for reversal routing' : 'Post directly when empty'}
                                disabled={isErrorCorrectionType}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="branchCode" label="Branch Restriction">
                            <Input placeholder="Leave empty for all branches" />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            <Card title="Settings" size="small" className="mb-4">
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="forceWorkflowApproval"
                            label={isErrorCorrectionType ? 'Pass reversal through Action Flow' : 'Always require Action Flow'}
                            valuePropName="checked"
                            tooltip={isErrorCorrectionType ? 'When enabled, every reversal request will go through Action Flow regardless of amount.' : 'When enabled, this transaction always goes through Action Flow regardless of amount.'}
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="printReceipt"
                            label="Print Receipt"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="active"
                            label="Active"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            <Form.Item>
                <Space>
                    <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={isSubmitting}
                    >
                        {isEditing ? 'Update' : 'Create'} Menu
                    </Button>
                    <Button
                        onClick={onCancel}
                        icon={<CloseOutlined />}
                    >
                        Cancel
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};
